import React, { useState, useEffect, useCallback, useRef }  from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import { Row, Col, Button, Card, CardBody } from "reactstrap";
import { resetShift, loadShift, createShift, updateShift } from '../../redux';
import ShiftSuggestion from './ShiftSuggestion';
import ShiftForm from './ShiftForm';
import moment from 'moment';
import {
    PLAN_EVENT_SCREENING,
    planSectorIdFromResource,
    planSlotIsCreatable,
    shiftCalendarEvents,
} from './planCalendarEvents';
import { buildShiftDuplicatePayload } from './shiftDuplicate';
import { buildShiftDraftFromSlot, isShiftDraftId } from './shiftDraft';
import PlanEventPeople from './PlanEventPeople';
import ScreeningOpaqueBadges from '../ScreeningOpaqueBadges';
import {
    screenings_calendar_range_api_params,
    screenings_calendar_view_range,
} from '../../containers/Screenings/ScreeningsList/screeningsCalendarRange';
import { Utils } from '../../services';
import { shiftContactAdmins } from '../../services/shiftContactAdmins';
import { first_poster_page_title_url } from '../../services/eventPoster';
import { TaskUserLabel } from '../../containers/Tasks/taskUserDisplay';
import user_avatar from '../../assets/images/user_avatar.webp';

import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './Planner.css';

const DnDCalendar = withDragAndDrop(Calendar)

const localizer = momentLocalizer(moment);

function screeningAssignees(event) {
    return (event?.tasks || [])
        .map((task) => task?.user)
        .filter((user) => user && (user.id || user.fullname));
}

const Event = ({ event }) => {
    if (event.kind === PLAN_EVENT_SCREENING) {
        return (
            <div className="event">
                {(event.title || event.opaque) && (
                    <div className="Planner__event-title">
                        {event.title}
                        <ScreeningOpaqueBadges opaque={event.opaque} className="calendar-opaque-badge" />
                    </div>
                )}
                <div className="my-1">
                    <b className="no-wrap">
                        <i className="fa fa-user" />&nbsp;
                        { event.assigned } / { event.total }
                    </b>
                    {event.capacity > 0 && (
                        <div className="no-wrap">
                            <i className="fa fa-ticket" />&nbsp;
                            { event.booked || 0 } / { event.capacity }
                        </div>
                    )}
                </div>
                <PlanEventPeople people={screeningAssignees(event)} />
            </div>
        );
    }

    return (
        <div className="event">
            <div className="my-1">
                <b className="no-wrap">
                    <i className="fa fa-user" />&nbsp;
                    { event.shift?.nb_assigned } / { event.shift?.nb_volunteers }
                </b>
            </div>
            <PlanEventPeople
                people={event.shift?.assignees || []}
                admins={shiftContactAdmins(event.shift)}
            />
        </div>
    );
}

function activityLabel(task, lang) {
    return Utils.localized_or_fallback(task?.activity?.name, lang) || '';
}

function ScreeningDetails({ event, t, i18n, canSeeScreening, canSeeTasks, onClose }) {
    const posterUrl = event.posterUrl
        ? first_poster_page_title_url([{ url: event.posterUrl }], 96, 144)
        : null;
    const tasks = [...(event.tasks || [])].sort((a, b) => {
        const byActivity = activityLabel(a, i18n.language).localeCompare(
            activityLabel(b, i18n.language)
        );
        if (byActivity !== 0)
            return byActivity;
        const aName = a.user?.fullname || '';
        const bName = b.user?.fullname || '';
        if (!aName && bName)
            return 1;
        if (aName && !bName)
            return -1;
        return aName.localeCompare(bName);
    });

    return (
        <div className="event-details-wrapper">
            <Card className="event-details-content">
                <CardBody>
                    <div className="Planner__screening-header">
                        {posterUrl && (
                            <img
                                className="Planner__screening-poster"
                                src={posterUrl}
                                alt=""
                            />
                        )}
                        <div className="Planner__screening-meta">
                            <h4 className="Planner__screening-title">
                                { event.title || t('plan.screening') }
                                <ScreeningOpaqueBadges opaque={event.opaque} className="ml-1" />
                            </h4>
                            {(event.start || event.end) && (
                                <p>
                                    <i className="fa fa-clock-o" />&nbsp;
                                    {event.start && Utils.formatLocalizedDateTime(event.start, i18n.language, 'LT')}
                                    {event.start && event.end ? ' – ' : ''}
                                    {event.end && Utils.formatLocalizedDateTime(event.end, i18n.language, 'LT')}
                                </p>
                            )}
                            {event.placeTitle && (
                                <p>
                                    <i className="fa fa-map-marker" />&nbsp;
                                    { event.placeTitle }
                                </p>
                            )}
                        </div>
                    </div>
                    <p>
                        <i className="fa fa-user" />&nbsp;
                        { event.assigned } / { event.total }
                    </p>
                    {event.capacity > 0 && (
                        <p>
                            <i className="fa fa-ticket" />&nbsp;
                            { event.booked || 0 } / { event.capacity }
                        </p>
                    )}
                    {tasks.length > 0 && (
                        <>
                            <h5>{ t('plan.tasks_heading') }</h5>
                            {tasks.map((task) => (
                                <div key={task.id || `${task.activity_id}-${task.user_id}`} className="Planner__task-row">
                                    <div className="Planner__task-row-avatar">
                                        <img
                                            src={task.user?.avatar || user_avatar}
                                            alt=""
                                        />
                                    </div>
                                    <div className="Planner__task-row-infos">
                                        <div className="Planner__task-row-activity">
                                            {activityLabel(task, i18n.language) || '—'}
                                        </div>
                                        <div className="Planner__task-row-status">
                                            {task.status ? t(`tasks.status_${task.status}`) : ''}
                                            {' · '}
                                            {task.user?.fullname ? (
                                                <TaskUserLabel user={task.user} t={t} />
                                            ) : (
                                                t('plan.unassigned')
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                    {(canSeeScreening || canSeeTasks) && (
                        <p className="mt-3">
                            {canSeeScreening && (
                                <Link
                                    className="btn btn-primary"
                                    to={ `/screenings/edit/${encodeURIComponent(event.screeningId)}` }
                                >
                                    { t('plan.see_screening') }
                                </Link>
                            )}
                            {canSeeTasks && (
                                <Link
                                    className={`btn ${canSeeScreening ? 'btn-secondary ml-2' : 'btn-primary'}`}
                                    to={ `/tasks?screening_id=${encodeURIComponent(event.screeningId)}` }
                                >
                                    { t('plan.see_tasks') }
                                </Link>
                            )}
                        </p>
                    )}
                </CardBody>
            </Card>
            <div className="event-details-closer">
                <Button color="danger" size="md" onClick={onClose}>
                    <i className="fa fa-times" />
                </Button>
            </div>
        </div>
    );
}

const Planner = ({
    resources,
    tasks,
    applicationType,
    sectors,
    activities,
    users,
    currentDate,
    calendarView = Views.DAY,
    initialShiftId,
    onVisibleRangeChange,
    onCalendarViewChange,
    onCalendarDateChange,
}) => {
    const dispatch    = useDispatch();
    const { t, i18n } = useTranslation();
    const user        = useSelector(state => state.auth.user);

    const [ date, setDate ]                                 = useState(currentDate);
    const [ view, setView ]                                 = useState(calendarView || Views.DAY);
    const [ currentShiftId, setCurrentShiftId ]             = useState(null);
    const [ currentPreviewUserId, setCurrentPreviewUserId ] = useState(null);
    const [ selectedScreening, setSelectedScreening ]       = useState(null);
    const [ optimisticUpdates, setOptimisticUpdates ]      = useState({});
    const [ optimisticDuplicates, setOptimisticDuplicates ] = useState([]);
    const [ draftShift, setDraftShift ]                     = useState(null);
    const [ copyDragActive, setCopyDragActive ]             = useState(false);
    const pendingSelectCleanupRef                           = useRef(null);
    const lastRangeKeyRef                                   = useRef('');
    const altKeyHeldRef                                     = useRef(false);
    const isDraggingRef                                     = useRef(false);
    const isCopyDragRef                                     = useRef(false);

    const { shift, isLoadingOne, loadingErrorMessage } = useSelector(state => state.shifts);

    const setCopyDrag = useCallback((active) => {
        isCopyDragRef.current = active;
        setCopyDragActive(active);
    }, []);

    const emitVisibleRange = useCallback((range) => {
        const params = screenings_calendar_range_api_params(range);
        if (!params || typeof onVisibleRangeChange !== 'function')
            return;
        const key = `${params.stop_at_gte}|${params.start_at_lte}`;
        if (key === lastRangeKeyRef.current)
            return;
        lastRangeKeyRef.current = key;
        onVisibleRangeChange(params);
    }, [onVisibleRangeChange]);

    useEffect(() => {
        if (currentDate)
            setDate(currentDate)
    }, [ currentDate ]);

    useEffect(() => {
        if (calendarView && calendarView !== view)
            setView(calendarView);
        // Sync from parent prefs/URL only; local onView still updates immediately.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarView]);

    useEffect(() => {
        const fallback = screenings_calendar_view_range(currentDate || new Date(), view);
        emitVisibleRange(fallback);
        // First paint / currentDate change only; later navigations go through onRangeChange.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDate]);

    useEffect(() => {
        const syncAltKey = (e) => {
            const held = !!e.altKey;
            altKeyHeldRef.current = held;
            if (isDraggingRef.current)
                setCopyDragActive(held);
        };
        const clearAltKey = () => {
            altKeyHeldRef.current = false;
            if (isDraggingRef.current)
                setCopyDragActive(false);
        };
        const onMouseUp = (e) => {
            if (isDraggingRef.current) {
                // Snapshot before RBC handles the drop (keyup can fire first on some OS).
                isCopyDragRef.current = !!e.altKey;
                setCopyDragActive(!!e.altKey);
            }
            requestAnimationFrame(() => {
                isDraggingRef.current = false;
                isCopyDragRef.current = false;
                setCopyDragActive(false);
            });
        };
        window.addEventListener('keydown', syncAltKey, true);
        window.addEventListener('keyup', syncAltKey, true);
        window.addEventListener('mousemove', syncAltKey, true);
        window.addEventListener('blur', clearAltKey);
        window.addEventListener('mouseup', onMouseUp, true);
        return () => {
            window.removeEventListener('keydown', syncAltKey, true);
            window.removeEventListener('keyup', syncAltKey, true);
            window.removeEventListener('mousemove', syncAltKey, true);
            window.removeEventListener('blur', clearAltKey);
            window.removeEventListener('mouseup', onMouseUp, true);
        };
    }, []);

    const onShowSuggestion = userId => {
        setCurrentPreviewUserId(userId === currentPreviewUserId ? null : userId);
    };

    const messages = React.useMemo(() => ({
        week: t('plan.calendar.week'),
        work_week: t('plan.calendar.work_week'),
        day: t('plan.calendar.day'),
        month: t('plan.calendar.month'),
        previous: t('plan.calendar.previous'),
        next: t('plan.calendar.next'),
        today: t('plan.calendar.today'),
        agenda: t('plan.calendar.agenda'),
        showMore: (total) => t('plan.calendar.showMore', { total })
    }), [t]);

    const eventPropGetter = useCallback(
        (event, _start, _end, _isSelected) => {
            if (event.kind === PLAN_EVENT_SCREENING) {
                const assigned = event.assigned || 0;
                const total = event.total || 0;
                if (assigned === 0 && total > 0)
                    return { className: 'screening empty' };
                if (assigned > 0 && assigned < total)
                    return { className: 'screening filling' };
                if (total > 0 && assigned === total)
                    return { className: 'screening full' };
                return { className: 'screening' };
            }

            if (event.shift?.isDraft || isShiftDraftId(event?.id))
                return { className: 'draft' };

            return {
                ...((event.shift?.nb_assigned === 0 && event.shift?.nb_volunteers > 0) && {
                    className: 'empty',
                }),
                ...((event.shift?.nb_assigned > 0 && event.shift?.nb_assigned < event.shift?.nb_volunteers) && {
                    className: 'filling',
                }),
                ...((event.shift?.nb_assigned === event.shift?.nb_volunteers) && {
                    className: 'full',
                }),
            };
        },
        []
    )

    const onNavigate = (nextDate) => {
        setDate(nextDate);
        if (typeof onCalendarDateChange === 'function')
            onCalendarDateChange(nextDate);
    }

    const onView = (nextView) => {
        setView(nextView);
        if (typeof onCalendarViewChange === 'function')
            onCalendarViewChange(nextView);
    }

    const selectShift = useCallback(shiftId => {
        setDraftShift(null);
        setSelectedScreening(null);
        setCurrentShiftId(shiftId);
        if( !shiftId)
            setCurrentPreviewUserId(null);
        dispatch(shiftId ? loadShift(shiftId) : resetShift());
    }, [dispatch])

    const selectScreening = useCallback(event => {
        setDraftShift(null);
        setCurrentShiftId(null);
        setCurrentPreviewUserId(null);
        dispatch(resetShift());
        setSelectedScreening(event || null);
    }, [dispatch]);

    const shiftPanelOpen = !!(shift || isLoadingOne || draftShift);

    useEffect(() => {
        if (!shiftPanelOpen)
            return undefined;
        const onKeyDown = (e) => {
            if (e.key !== 'Escape' || e.defaultPrevented)
                return;
            e.preventDefault();
            selectShift(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [shiftPanelOpen, selectShift]);

    const initial_shift_opened_ref = useRef(false);

    useEffect(() => {
        if (!initialShiftId || initial_shift_opened_ref.current) {
            return;
        }

        initial_shift_opened_ref.current = true;
        selectShift(initialShiftId);
    }, [initialShiftId, selectShift]);

    useEffect(() => {
        return () => {
            if (pendingSelectCleanupRef.current) {
                pendingSelectCleanupRef.current();
                pendingSelectCleanupRef.current = null;
            }
        };
    }, []);

    const onSelect = (event, uiEvent) => {
        if (event?.kind === PLAN_EVENT_SCREENING) {
            if (pendingSelectCleanupRef.current) {
                pendingSelectCleanupRef.current();
                pendingSelectCleanupRef.current = null;
            }
            selectScreening(event);
            return;
        }

        const shiftId = event?.id;
        if (!shiftId || isShiftDraftId(shiftId))
            return;

        if (pendingSelectCleanupRef.current) {
            pendingSelectCleanupRef.current();
            pendingSelectCleanupRef.current = null;
        }

        if (uiEvent?.type?.startsWith('mouse') && typeof window !== 'undefined') {
            const openOnRelease = () => {
                selectShift(shiftId);
                window.removeEventListener('mouseup', openOnRelease, true);
                pendingSelectCleanupRef.current = null;
            };
            window.addEventListener('mouseup', openOnRelease, true);
            pendingSelectCleanupRef.current = () => window.removeEventListener('mouseup', openOnRelease, true);
            return;
        }

        selectShift(shiftId);
    };

    const onDragStart = data => {
        const { event } = data;
        if (event?.kind === PLAN_EVENT_SCREENING)
            return;
        isDraggingRef.current = true;
        setCopyDrag(altKeyHeldRef.current);
        setCurrentShiftId(event.id);
    }

    const onEventResize = ({ start, end }) => {
        const shiftId = shift?.id || currentShiftId;
        if (!shiftId || String(shiftId).startsWith('screening:') || isShiftDraftId(shiftId))
            return;

        setOptimisticUpdates(previous => ({
            ...previous,
            [shiftId]: {
                ...previous[shiftId],
                start,
                end
            }
        }));

        dispatch(updateShift({
            id: shiftId,
            start_at: start,
            stop_at: end
        }, (err, _shift) => {
            setOptimisticUpdates(previous => {
                const next = { ...previous };
                if (err)
                    delete next[shiftId];
                else
                    delete next[shiftId];
                return next;
            });
        }));
    };

    const onEventDrop = ({ event, start, end, resourceId }) => {
        if (event?.kind === PLAN_EVENT_SCREENING)
            return false;

        const shiftId = event?.id || shift?.id || currentShiftId;
        if (!shiftId || String(shiftId).startsWith('screening:') || isShiftDraftId(shiftId))
            return false;

        const sectorId = resourceId ? planSectorIdFromResource(resourceId) : null;
        if (resourceId && !sectorId)
            return false;

        const isCopy = isCopyDragRef.current || altKeyHeldRef.current;
        isDraggingRef.current = false;

        if (isCopy) {
            const source = event?.shift
                || (shift?.id === shiftId ? shift : null);
            const payload = buildShiftDuplicatePayload(
                source,
                {
                    start_at: start,
                    stop_at: end,
                    ...(sectorId ? { sector_id: sectorId } : {}),
                },
                applicationType?.id
            );
            if (!payload)
                return false;

            const tempId = `optimistic-dup-${shiftId}-${Date.now()}`;
            const dropResourceId = sectorId
                ? (resourceId || event?.resourceId)
                : event?.resourceId;
            setOptimisticDuplicates(previous => [
                ...previous,
                {
                    id: tempId,
                    kind: event?.kind,
                    start,
                    end,
                    resourceId: dropResourceId,
                    title: event?.title,
                    shift: {
                        ...(source || {}),
                        id: tempId,
                        nb_assigned: 0,
                        start_at: start,
                        stop_at: end,
                        ...(sectorId ? { sector_id: sectorId } : {}),
                    },
                },
            ]);

            dispatch(createShift(payload, null, (err, created) => {
                setOptimisticDuplicates(previous => previous.filter(item => item.id !== tempId));
                if (!err && created?.id)
                    selectShift(created.id);
            }));
            return;
        }

        setOptimisticUpdates(previous => ({
            ...previous,
            [shiftId]: {
                ...previous[shiftId],
                start,
                end,
                ...(sectorId ? { resourceId } : {})
            }
        }));

        dispatch(updateShift({
            id: shiftId,
            start_at: start,
            stop_at: end,
            ...(sectorId ? { sector_id: sectorId } : {})
        }, (err, _shift) => {
            setOptimisticUpdates(previous => {
                const next = { ...previous };
                if (err)
                    delete next[shiftId];
                else
                    delete next[shiftId];
                return next;
            });
        }));
    };

    const updateEvent = (values) => {
        if (draftShift?.isDraft) {
            const payload = buildShiftDuplicatePayload(
                { ...draftShift, ...values },
                {},
                applicationType?.id
            );
            if (!payload)
                return;

            dispatch(createShift(payload, null, (err, created) => {
                if (!err && created?.id) {
                    setDraftShift(null);
                    selectShift(created.id);
                }
            }));
            return;
        }

        dispatch(updateShift({ ...values }));
    }

    const newEvent = React.useCallback(
        event => {
            if (!applicationType)
                return;

            if (shift || draftShift)
                return selectShift(null);

            const sectorId = planSectorIdFromResource(event.resourceId);
            if (!sectorId)
                return;

            const draft = buildShiftDraftFromSlot({
                applicationTypeId: applicationType.id,
                start: event.start,
                end: event.end,
                sectorId,
                activityId: activities?.length === 1 ? activities[0].id : null,
            });
            if (!draft)
                return;

            setSelectedScreening(null);
            setCurrentShiftId(null);
            setCurrentPreviewUserId(null);
            dispatch(resetShift());
            setDraftShift(draft);
        },
        [activities, applicationType, dispatch, draftShift, selectShift, shift]
    );

    const canCreateShifts = React.useMemo(
        () => (resources || []).some((resource) => planSectorIdFromResource(resource.id)),
        [resources]
    );

    const onSelecting = useCallback(
        (slot) => planSlotIsCreatable(slot?.resourceId),
        []
    );

    const isShiftEvent = useCallback((event) => {
        if (event?.kind === PLAN_EVENT_SCREENING)
            return false;
        if (event?.shift?.isDraft || isShiftDraftId(event?.id))
            return false;
        return true;
    }, []);

    const calendarTasks = React.useMemo(() => {
        const updated = (tasks || []).map(task => {
            const optimistic = optimisticUpdates[task.id];
            if (!optimistic)
                return task;
            return {
                ...task,
                ...(optimistic.start ? { start: optimistic.start } : {}),
                ...(optimistic.end ? { end: optimistic.end } : {}),
                ...(optimistic.resourceId ? { resourceId: optimistic.resourceId } : {})
            };
        });
        const withDuplicates = optimisticDuplicates.length
            ? updated.concat(optimisticDuplicates)
            : updated;
        if (!draftShift)
            return withDuplicates;
        return withDuplicates.concat(shiftCalendarEvents([draftShift]));
    }, [tasks, optimisticUpdates, optimisticDuplicates, draftShift]);

    const canSeeSelectedScreening = !!(
        selectedScreening?.screeningId
        && user?.canAccess?.(`/screenings/edit/${selectedScreening.screeningId}`)
    );
    const canSeeSelectedTasks = !!(
        selectedScreening?.screeningId && user?.canAccess?.('/tasks')
    );

    return (
        <div className={`Planner${copyDragActive ? ' Planner--copy-drag' : ''}`}>
            {loadingErrorMessage &&
                <div
                    className="alert alert-danger"
                    dangerouslySetInnerHTML={{__html: loadingErrorMessage}} />
            }

            <DnDCalendar
                culture='fr'
                localizer={ localizer }
                events={ calendarTasks }
                startAccessor="start"
                endAccessor="end"
                defaultView={ Views.DAY }
                view={ view }
                views={[Views.MONTH, Views.WEEK, Views.DAY]}
                date={ date }
                showMultiDayTimes
                draggableAccessor={ isShiftEvent }
                resizableAccessor={ isShiftEvent }
                onNavigate={ onNavigate }
                onView={ onView }
                onRangeChange={ emitVisibleRange }
                onSelectEvent={ onSelect }
                onDragStart={ onDragStart }
                onEventResize={ onEventResize }
                onEventDrop={ onEventDrop }
                onSelectSlot={ canCreateShifts ? newEvent : undefined }
                onSelecting={ canCreateShifts ? onSelecting : undefined }
                scrollToTime={ currentDate }
                step={ 15 }
                resizable
                selectable={ canCreateShifts }
                dayLayoutAlgorithm="no-overlap"
                messages={ messages }
                resources={ resources }
                components={{ event: Event }}
                eventPropGetter={ eventPropGetter }
            />
            { (shift || isLoadingOne || draftShift) && (
                <div className={`event-details-wrapper ${currentPreviewUserId ? 'with-preview' : ''}`}>
                    <Card className="event-details-content">
                        <CardBody className="event-details-body--shift">
                            <Row className="event-details-row">
                                {currentPreviewUserId && (
                                    <Col className="event-details-preview-col">
                                        <ShiftSuggestion userId={ currentPreviewUserId } />
                                    </Col>
                                )}
                                <Col className="event-details-shift-col">
                                    <ShiftForm
                                        onSubmit={ updateEvent }
                                        currentShiftId={currentShiftId}
                                        currentPreviewUserId={currentPreviewUserId}
                                        draftShift={ draftShift }
                                        onRemove={ err => !err && selectShift(null) }
                                        onDuplicated={ created => selectShift(created.id) }
                                        onShowSuggestion={ onShowSuggestion }
                                        sectors={ sectors }
                                        activities={ activities }
                                        users={ users }
                                        applicationTypeId={ applicationType?.id }
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                    <div className="event-details-closer">
                        <Button color="danger" size="md" onClick={() => selectShift(null) }>
                            <i className="fa fa-times" />
                        </Button>
                    </div>
                </div>
            )}
            { selectedScreening && (
                <ScreeningDetails
                    event={ selectedScreening }
                    t={ t }
                    i18n={ i18n }
                    canSeeScreening={ canSeeSelectedScreening }
                    canSeeTasks={ canSeeSelectedTasks }
                    onClose={ () => selectScreening(null) }
                />
            )}
        </div>
    );
}

export default Planner;
